import * as turf from '@turf/turf';

/**
 * Estimate default footprint area based on building type
 * Used when actual geometry isn't available (e.g., from Tilequery API)
 */
function estimateDefaultFootprintArea(buildingType: string): number {
  const defaultAreas: Record<string, number> = {
    'house': 120,           // Typical house ~120 sqm
    'residential': 150,     // General residential
    'apartments': 400,      // Apartment block
    'commercial': 500,      // Commercial building
    'industrial': 1000,     // Industrial unit
    'warehouse': 2000,      // Warehouse
    'office': 600,          // Office building
    'retail': 300,          // Retail store
    'hospital': 2000,       // Hospital wing
    'school': 800,          // School building
    'hotel': 500,           // Hotel
    'church': 300,          // Church
    'chapel': 150,          // Small chapel
    'garage': 50,           // Garage
    'shed': 20,             // Shed
    'barn': 200,            // Barn
  };

  return defaultAreas[buildingType?.toLowerCase()] || 150;
}

export function estimateBuildingHeight(building: any): number {
  const props = building.properties || {};
  const type = props.type || props.building || 'residential';
  const lowerType = type?.toLowerCase() || '';

  // Minimum credible heights by building type
  // Heights below these are likely placeholder/default values from the data source
  const minCredibleHeights: Record<string, number> = {
    'garage': 3,
    'shed': 2.5,
    'barn': 4,
    'hut': 2.5,
    'cabin': 3,
    'carport': 2.5,
    'roof': 3,
    'default': 5  // Most buildings are at least 5m (single story with roof)
  };

  const minCredible = minCredibleHeights[lowerType] || minCredibleHeights['default'];

  // 1. Check if height is directly available AND credible
  // Mapbox often has placeholder values like 3m for unknown heights
  if (props.height && props.height >= minCredible) {
    return props.height;
  }

  // 2. Check for building:levels from OSM
  if (props['building:levels'] && props['building:levels'] > 0) {
    return props['building:levels'] * 3.5; // ~3.5m per floor
  }

  // 3. Estimate from building type and footprint
  // Handle point geometries from Tilequery (which have no area)
  let area = 0;
  if (building.geometry && building.geometry.type !== 'Point') {
    area = turf.area(building.geometry);
  }

  // If area is too small (point geometry or missing), use default based on type
  if (area < 10) {
    area = estimateDefaultFootprintArea(type);
  }

  return estimateHeightFromType(type, area);
}

/**
 * Get building footprint area, with fallback for point geometries
 */
export function getBuildingFootprintArea(building: any): number {
  const props = building.properties || {};

  // Try to calculate from geometry if it's a polygon
  if (building.geometry && building.geometry.type !== 'Point') {
    const area = turf.area(building.geometry);
    if (area > 10) return area;
  }

  // Fall back to default based on type
  const type = props.type || props.building || 'residential';
  return estimateDefaultFootprintArea(type);
}

function estimateHeightFromType(buildingType: string, footprintArea: number): number {
  const estimates: Record<string, { minHeight: number; heightPerArea: number }> = {
    'house': { minHeight: 7, heightPerArea: 0 },
    'residential': { minHeight: 10, heightPerArea: 0.001 },
    'apartments': { minHeight: 15, heightPerArea: 0.002 },
    'commercial': { minHeight: 12, heightPerArea: 0.003 },
    'industrial': { minHeight: 8, heightPerArea: 0.001 },
    'warehouse': { minHeight: 10, heightPerArea: 0 },
    'office': { minHeight: 15, heightPerArea: 0.005 },
    'retail': { minHeight: 6, heightPerArea: 0.001 },
    'hospital': { minHeight: 20, heightPerArea: 0.003 },
    'school': { minHeight: 12, heightPerArea: 0.002 },
    'hotel': { minHeight: 15, heightPerArea: 0.004 }
  };
  
  const estimate = estimates[buildingType.toLowerCase()] || estimates['residential'];
  const estimatedHeight = estimate.minHeight + (footprintArea * estimate.heightPerArea);
  
  return Math.min(estimatedHeight, 150);
}

export function estimateBuildingMass(volume: number, buildingType?: string): number {
  const densities: Record<string, number> = {
    'residential': 150,
    'commercial': 200,
    'office': 250,
    'industrial': 180,
    'warehouse': 120
  };
  
  const density = densities[buildingType?.toLowerCase() || 'residential'] || 150;
  return (volume * density) / 1000; // tonnes
}

export function estimateFoundationDepth(
  height: number,
  mass: number,
  footprintArea: number
): number {
  // Foundation depth is primarily based on building height
  // Taller buildings need deeper foundations for stability

  // Very tall buildings (50m+) - deep piled foundations
  if (height >= 50) {
    return Math.min(height * 0.15, 20); // 15% of height, max 20m
  }

  // Tall buildings (20-50m) - piled foundations
  if (height >= 20) {
    return 3 + (height - 20) * 0.1; // 3m base + 0.1m per meter above 20m = 3-6m
  }

  // Medium buildings (10-20m) - deeper strip/pad foundations
  if (height >= 10) {
    return 1.5 + (height - 10) * 0.15; // 1.5m base + 0.15m per meter above 10m = 1.5-3m
  }

  // Small buildings (<10m) - shallow foundations
  return 1.2 + height * 0.05; // 1.2-1.7m for buildings up to 10m
}

export function estimateWaterwayWidth(waterwayClass: string): number {
  const widths: Record<string, number> = {
    'river': 30,
    'stream': 5,
    'canal': 15,
    'ditch': 2,
    'drain': 1
  };

  return widths[waterwayClass] || 10;
}

/**
 * Estimate water depth for large bodies of water
 * Based on typical depths for different water body types
 */
export function estimateWaterDepth(waterClass: string): number {
  const depths: Record<string, number> = {
    'ocean': 50,      // Coastal areas - varies greatly
    'sea': 30,        // Seas and large bays
    'lake': 10,       // Average lake depth
    'river': 8,       // Large river (as polygon)
    'reservoir': 15,  // Reservoirs tend to be deeper
    'pond': 3,        // Small ponds
    'dock': 10,       // Harbor/dock areas
    'harbour': 12,    // Harbor areas
    'estuary': 10,    // Estuaries - tidal, variable
  };

  return depths[waterClass?.toLowerCase()] || 10;
}

/**
 * Estimate road width based on road classification
 * Important for HDD planning - roads carry traffic loads
 */
export function estimateRoadWidth(roadClass: string): number {
  const widths: Record<string, number> = {
    'motorway': 35,       // Dual carriageway with hard shoulders
    'motorway_link': 10,  // Slip roads
    'trunk': 25,          // Major A-roads, often dual
    'trunk_link': 8,      // Slip roads for trunk
    'primary': 15,        // A-roads
    'primary_link': 7,
    'secondary': 12,      // B-roads
    'secondary_link': 6,
    'tertiary': 8,        // Local connecting roads
    'tertiary_link': 5,
    'residential': 6,     // Residential streets
    'unclassified': 5,    // Minor roads
    'service': 4,         // Access roads, parking
    'track': 3,           // Farm/forest tracks
    'path': 2,            // Footpaths
    'footway': 2,
    'cycleway': 3,
    'pedestrian': 4,      // Pedestrian areas
    'living_street': 5,   // Shared space streets
  };

  return widths[roadClass?.toLowerCase()] || 6;
}

/**
 * Estimate railway corridor width based on railway type
 * Critical for HDD - railways have strict clearance requirements
 */
export function estimateRailwayWidth(railwayClass: string): number {
  const widths: Record<string, number> = {
    'rail': 12,           // Main line railway (includes safety zones)
    'narrow_gauge': 8,    // Narrow gauge railway
    'light_rail': 8,      // Light rail/tram on dedicated ROW
    'subway': 10,         // Underground (but may have surface sections)
    'tram': 6,            // Tram on street
    'monorail': 6,        // Monorail
    'preserved': 10,      // Heritage railways
    'miniature': 3,       // Miniature railways
    'disused': 8,         // Disused (may be reinstated)
    'abandoned': 6,       // Abandoned (corridor may still exist)
    'construction': 12,   // Under construction
  };

  return widths[railwayClass?.toLowerCase()] || 10;
}

/**
 * Road pavement structure depths by road class
 * Includes: surface course, binder course, base course, sub-base
 * Based on UK Highways England standards (DMRB)
 */
export function estimateRoadPavementDepth(roadClass: string): number {
  const depths: Record<string, number> = {
    'motorway': 1.2,        // Heavy duty pavement: 50mm surface + 60mm binder + 200mm base + 450mm sub-base + capping
    'motorway_link': 1.0,
    'trunk': 1.0,           // Major A-roads
    'trunk_link': 0.8,
    'primary': 0.8,         // A-roads: 40mm surface + 60mm binder + 150mm base + 300mm sub-base
    'primary_link': 0.7,
    'secondary': 0.6,       // B-roads: lighter construction
    'secondary_link': 0.5,
    'tertiary': 0.5,        // Local roads
    'tertiary_link': 0.4,
    'residential': 0.4,     // Residential streets: 40mm surface + 60mm binder + 100mm base + 150mm sub-base
    'unclassified': 0.35,
    'service': 0.3,         // Access roads, car parks
    'track': 0.2,           // Unpaved/lightly paved
    'path': 0.1,
    'footway': 0.15,
    'cycleway': 0.2,
    'pedestrian': 0.2,
    'living_street': 0.4,
  };

  return depths[roadClass?.toLowerCase()] || 0.4;
}

/**
 * Estimate maximum utility depth under roads
 * Major roads typically have: sewers, water mains, gas, telecoms, electricity
 * Returns the deepest expected utility depth
 */
export function estimateRoadUtilityDepth(roadClass: string): number {
  // Typical utility burial depths (UK standards):
  // - Electricity HV: 0.6-1.0m
  // - Electricity LV: 0.45-0.6m
  // - Gas mains: 0.75-1.2m (high pressure deeper)
  // - Water mains: 0.75-1.35m (frost protection)
  // - Telecoms: 0.35-0.6m
  // - Sewers: 1.0-6.0m+ (gravity flow, varies greatly)

  // Major roads tend to have larger/deeper sewers
  const depths: Record<string, number> = {
    'motorway': 3.5,        // Major trunk sewers, multiple utilities
    'motorway_link': 2.5,
    'trunk': 3.0,           // Trunk sewers likely
    'trunk_link': 2.5,
    'primary': 2.5,         // A-roads often have combined sewers
    'primary_link': 2.0,
    'secondary': 2.0,       // B-roads
    'secondary_link': 1.8,
    'tertiary': 1.8,        // Local distributor roads
    'tertiary_link': 1.5,
    'residential': 1.5,     // Foul + surface water sewers, all utilities
    'unclassified': 1.2,
    'service': 1.0,         // May have utilities for adjacent buildings
    'track': 0.0,           // Unlikely to have utilities
    'path': 0.0,
    'footway': 0.8,         // May have telecoms/electricity
    'cycleway': 0.5,
    'pedestrian': 1.0,      // Town center - may have utilities
    'living_street': 1.5,
  };

  return depths[roadClass?.toLowerCase()] || 1.5;
}

/**
 * Get details about likely utilities under a road
 */
export function getRoadUtilityInfo(roadClass: string): {
  likelyUtilities: string[];
  maxDepth: number;
  description: string;
} {
  const lowerClass = roadClass?.toLowerCase() || 'residential';

  // Major roads have more utilities
  const majorRoads = ['motorway', 'trunk', 'primary', 'secondary'];
  const urbanRoads = ['residential', 'tertiary', 'living_street', 'pedestrian'];

  let likelyUtilities: string[] = [];
  let description = '';

  if (majorRoads.some(r => lowerClass.includes(r))) {
    likelyUtilities = ['Trunk sewer', 'Water main', 'Gas main', 'HV electricity', 'Telecoms'];
    description = 'Major road - likely to have significant buried infrastructure including trunk sewers';
  } else if (urbanRoads.some(r => lowerClass.includes(r))) {
    likelyUtilities = ['Sewer', 'Water main', 'Gas', 'Electricity', 'Telecoms'];
    description = 'Urban road - standard utility installation expected';
  } else if (lowerClass === 'service') {
    likelyUtilities = ['Service connections'];
    description = 'Service road - may have utility connections to adjacent buildings';
  } else if (['track', 'path'].includes(lowerClass)) {
    likelyUtilities = [];
    description = 'Unlikely to have buried utilities';
  } else {
    likelyUtilities = ['Possible utilities'];
    description = 'Unknown utility status - survey recommended';
  }

  return {
    likelyUtilities,
    maxDepth: estimateRoadUtilityDepth(roadClass),
    description
  };
}
